import React, { useState, useEffect, useCallback } from 'react';
import type { AboutProps, SkillRecord } from './about.types';
import AboutTelemetry from './components/AboutTelemetry';
import SkillDrum from './components/SkillDrum';
import rawSkillsData from '../../data/skills.json';
import './About.styles.css';

// * Cast skills dataset safely to readonly SkillRecord array
const SKILLS: readonly SkillRecord[] = rawSkillsData as readonly SkillRecord[];

// * [COMPONENT] Interactive Skills & Architecture section featuring 3D Drum and Telemetry HUD
export const About: React.FC<AboutProps> = ({
  className = '',
  isActive = true,
  scale: propScale,
}) => {
  const [activeSkillIndex, setActiveSkillIndex] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 900 : false
  );

  // * Dynamic element and font scaling fallback
  const [localScale] = useState<number>(() => {
    try {
      if (typeof window !== 'undefined') {
        const saved =
          localStorage.getItem('ui_scale') ||
          localStorage.getItem('about_ui_scale');
        if (saved) return parseFloat(saved);
        return window.innerWidth < 900 ? 0.85 : 1.0;
      }
    } catch {
      // Fallback
    }
    return 1.0;
  });

  const effectiveScale = propScale ?? localScale;

  // * Responsive screen width listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // * Handle skill selection and synchronization from the 3D rotating drum
  const handleSelectSkill = useCallback((index: number) => {
    setActiveSkillIndex(index);
  }, []);

  const currentSkill: SkillRecord | null = SKILLS[activeSkillIndex] || SKILLS[0] || null;

  return (
    <section
      className={`about-section ${className}`}
      id="about"
      aria-label="Interactive Skills & Architecture Telemetry"
    >
      {/* * Dual-Panel Dynamic Layout (Left: Telemetry HUD, Right: 3D Drum) */}
      <div
        className="about-layout-grid"
        style={{
          transform: `scale(${effectiveScale})`,
          transformOrigin: isMobile ? 'top center' : 'center center',
          transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* * Left: Dynamic Scaling Telemetry Panel */}
        <div className="about-col-telemetry">
          <AboutTelemetry selectedSkill={currentSkill} />
        </div>

        {/* * Right: 3D X-Axis Rotating Skill Drum */}
        <div className="about-col-drum">
          <SkillDrum
            skills={SKILLS}
            activeIndex={activeSkillIndex}
            onSelectSkill={handleSelectSkill}
            isSectionActive={isActive}
          />
        </div>
      </div>
    </section>
  );
};

export default About;
